import type { Swimlane } from "@/model/board";
import type { RelativeCoordinate } from "@/model/coordinates";
import { useBoardsStore } from "@/store/boards";
import { useClientSettingsStore } from "@/store/clientSettings";
import { useIterationStore } from "@/store/iteration";
import { useSessionStore } from "@/store/session";
import { useStickyTypeStore } from "@/store/stickyType";
import { useTeamStore } from "@/store/team";
import { useGlobalI18n } from "@/translations/i18n";

import { BasePlanningBoard } from "./BasePlanningBoard";
import type { GroupIterations } from "./location/PlanningBoardLocation";
import { PlanningBoardLocation } from "./location/PlanningBoardLocation";

const { t } = useGlobalI18n();

export class ProgramBoard extends BasePlanningBoard<"program"> {
  override get groups() {
    const teamIterations = Object.fromEntries(
      useBoardsStore()
        .artTeamBoards()
        .map((board) => [
          board.team.id,
          useIterationStore()
            .iterations(board)
            .map((bi, index) => ({
              load: bi.load,
              velocity: bi.velocity,
              statusDistribution: bi.statusDistribution,
              ...useSessionStore().iterations[index],
            })),
        ]),
    );

    return this.board.swimlanes.map(group);

    function group(lane: Swimlane): GroupIterations {
      switch (lane.type) {
        case "team": {
          const team = useTeamStore().findTeam({ id: lane.teamIds[0] })!;
          return {
            id: team.id,
            name: team.name,
            current: team.id === useTeamStore().current.id,
            iterations: teamIterations[team.id] || [], // This function can run before boards are loaded
          };
        }
        case "external": {
          const team = useTeamStore().findTeam({ id: lane.teamIds[0], withExternal: true })!;
          return {
            id: lane.teamIds[0],
            name: lane.name || team.name,
            iterations: teamIterations[team.id] || [],
            externalOrgIds: lane.teamIds,
          };
        }
        case "milestones":
          return {
            id: "",
            name: t("programBoard.milestoneEvents"),
            iterations: useSessionStore().iterations,
          };
        case "otherArts":
          return {
            id: "",
            name: t("programBoard.otherArts"),
            iterations: useSessionStore().iterations,
          };
        case "empty":
          return { id: "", name: "", iterations: useSessionStore().iterations };
      }
    }
  }

  location(pos: RelativeCoordinate | number | string, top?: number) {
    return PlanningBoardLocation.ofTeams(this.groups, pos, top);
  }

  override positionalCardProperties(pos: RelativeCoordinate) {
    const loc = this.location(pos);
    return { iterationId: loc.iterationId, teamId: loc.groupId };
  }

  override get showPermanentLinks() {
    return useClientSettingsStore().permanentProgramLinks;
  }

  get possibleStickyTypes() {
    return useStickyTypeStore().nonFlexBoardStickyTypes("program");
  }
}
